var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Children, useState, useEffect, cloneElement } from 'react';
import { usePreviousState, safeRest } from '@telus/koodo-shared-utils';
import { useViewport, FlexGrid } from '@telus-uds/components-web';
import styled from 'styled-components';
import { FeaturesPanel } from './FeaturesPanel';
export var AccordionList = function (_a) {
    var children = _a.children, defaultFeature = _a.defaultFeature, _b = _a.defaultBorderColor, defaultBorderColor = _b === void 0 ? 'black' : _b, _c = _a.repeatDefault, repeatDefault = _c === void 0 ? false : _c, openPanel = _a.openPanel, _d = _a.accessOpenPanels, accessOpenPanels = _d === void 0 ? function () { } : _d, rest = __rest(_a, ["children", "defaultFeature", "defaultBorderColor", "repeatDefault", "openPanel", "accessOpenPanels"]);
    // An array of all the ids for the children passed into the component.
    var planIds = Children.map(children, function (child) { return child.props.id; });
    // A function to create an array the length plansId filled with a value of false.
    var closeAllPanels = function () { return Array(planIds.length).fill(false); };
    // If there is a default panel to be opened, initialize the openPanelsBool state with the value of that index set to true
    var defaultPanelLoc = openPanel && planIds.indexOf(openPanel);
    var openPanelsBoolWithDefault = closeAllPanels();
    if (defaultPanelLoc) {
        openPanelsBoolWithDefault[defaultPanelLoc] = true;
    }
    var _e = useState(), fade = _e[0], setFade = _e[1];
    var _f = useState(openPanel ? [openPanel] : ['default']), openPanels = _f[0], setOpenPanels = _f[1];
    /* An array of boolean values containing the open/closed state for each panel. Not directly used in the AccordionList component.
     *  Its purpose is to expose the selected state of each panel through the accessOpenPanels callback to the parent component if necessary. */
    var _g = useState(openPanel ? openPanelsBoolWithDefault : closeAllPanels()), openPanelsBool = _g[0], setOpenPanelsBool = _g[1];
    // Helper to identify if the current item is the last item in a accordion section to remove the bottom padding
    var isCurrentItemLast = function (isLast, index) {
        return typeof isLast !== 'undefined' ? !!isLast : index === children.length - 1;
    };
    var viewport = useViewport();
    useEffect(function () {
        accessOpenPanels(openPanelsBool);
    }, [openPanelsBool]);
    /** Used for setting default open */
    var previousOpenPanel = usePreviousState(openPanel);
    useEffect(function () {
        if (previousOpenPanel !== openPanel) {
            setOpenPanels([openPanel]);
            var newOpenPanelBool = closeAllPanels();
            newOpenPanelBool[planIds.indexOf(openPanel)] = true;
            setOpenPanelsBool(newOpenPanelBool);
        }
    }, [openPanel]);
    var handleOnClickItem = function (id, desktop) {
        setFade(true);
        var idIndex = planIds.indexOf(id);
        if (!desktop) {
            var newOpenPanels = openPanels[0] === 'default' ? [] : __spreadArray([], openPanels, true);
            var newOpenPanelsBool = __spreadArray([], openPanelsBool, true);
            if (newOpenPanels.includes(id)) {
                setOpenPanels(newOpenPanels.filter(function (panel) { return panel !== id; }));
                newOpenPanelsBool[idIndex] = false;
            }
            else {
                setOpenPanels(__spreadArray(__spreadArray([], newOpenPanels, true), [id], false));
                if (idIndex >= 0)
                    newOpenPanelsBool[idIndex] = true;
            }
            setOpenPanelsBool(newOpenPanelsBool);
        }
        else {
            var newOpenPanels = __spreadArray([], openPanels, true);
            var selectedId = newOpenPanels.pop();
            if (repeatDefault && selectedId === id) {
                newOpenPanels.push('default');
                setOpenPanelsBool(closeAllPanels());
            }
            else {
                newOpenPanels.push(id);
                var newOpenPanelsBool = closeAllPanels();
                if (idIndex >= 0)
                    newOpenPanelsBool[idIndex] = true;
                if (selectedId !== id) {
                    setOpenPanelsBool(newOpenPanelsBool);
                }
            }
            if (selectedId !== id) {
                setOpenPanels(newOpenPanels);
            }
        }
    };
    /**
     * If multiple panels are open, close all panels and sets to default when changing between mobile and desktop.
     */
    useEffect(function () {
        if (openPanels.length > 1 || openPanels.length === 0) {
            setOpenPanels(['default']);
            setOpenPanelsBool(closeAllPanels());
        }
    }, [viewport]);
    var isDesktop = ['md', 'lg', 'xl'].includes(viewport);
    var ConditionalWrapper = function (_a) {
        var condition = _a.condition, wrapper = _a.wrapper, children = _a.children;
        return (condition ? wrapper(children) : children);
    };
    return (_jsx(_Fragment, { children: isDesktop ? (_jsx(FlexGridWrapper, { children: _jsx(FlexGrid, { limitWidth: false, gutter: false, children: _jsxs(StyledContainer, __assign({}, safeRest(rest), { children: [openPanels[0] === 'default' && defaultFeature && (_jsxs(FlexGrid.Row, { mdReverse: true, children: [_jsx(FlexGrid.Col, { xs: 12, md: 6, children: _jsx(StyledFeaturesPanelWrapper, { children: _jsx(FeaturesPanel, { theme: "default", defaultBorderColor: defaultBorderColor, isDefault: true, selectedFeature: defaultFeature }) }) }), _jsx(FlexGrid.Col, { xs: 12, md: 6, children: Children.map(children, function (child, index) {
                                        var _a;
                                        var isLastItem = (_a = child === null || child === void 0 ? void 0 : child.props) === null || _a === void 0 ? void 0 : _a.isLastItem;
                                        // If the child is not of type AccordionList.Item then don't pass props.
                                        if (!child.props.features) {
                                            return child;
                                        }
                                        return cloneElement(child, {
                                            handleOnClickItem: handleOnClickItem,
                                            openPanels: openPanels,
                                            fade: fade,
                                            isLastItem: isCurrentItemLast(isLastItem, index)
                                        });
                                    }) })] })), !defaultFeature ||
                            (defaultFeature && openPanels[0] !== 'default' && (_jsx(ConditionalWrapper, { condition: isDesktop, wrapper: function (children) { return _jsx(StyledWrapper, { children: children }); }, children: Children.map(children, function (child, index) {
                                    var _a;
                                    var isLastItem = (_a = child === null || child === void 0 ? void 0 : child.props) === null || _a === void 0 ? void 0 : _a.isLastItem;
                                    // If the child is not of type AccordionList.Item then don't pass props.
                                    if (!child.props.features) {
                                        return child;
                                    }
                                    return cloneElement(child, {
                                        handleOnClickItem: handleOnClickItem,
                                        openPanels: openPanels,
                                        fade: fade,
                                        isLastItem: isCurrentItemLast(isLastItem, index)
                                    });
                                }) })))] })) }) })) : (_jsx(FlexGrid, { limitWidth: false, gutter: false, children: _jsx(StyledContainer, __assign({}, safeRest(rest), { children: Children.map(children, function (child) {
                    // If the child is not of type AccordionList.Item then don't pass props.
                    if (!child.props.features) {
                        return child;
                    }
                    return cloneElement(child, {
                        handleOnClickItem: handleOnClickItem,
                        openPanels: openPanels,
                        fade: fade
                    });
                }) })) })) }));
};
// --------------------------- STYLES --------------------------- //
var FlexGridWrapper = styled.div(templateObject_1 || (templateObject_1 = __makeTemplateObject(["\n  > div {\n    flex-wrap: initial !important;\n  }\n"], ["\n  > div {\n    flex-wrap: initial !important;\n  }\n"])));
var StyledContainer = styled.div(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n  position: relative;\n"], ["\n  position: relative;\n"])));
var StyledWrapper = styled.div(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  :first-child {\n    display: grid;\n    grid-template-columns: 50% 50%;\n  }\n"], ["\n  :first-child {\n    display: grid;\n    grid-template-columns: 50% 50%;\n  }\n"])));
var StyledFeaturesPanelWrapper = styled.div(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  height: 100%;\n"], ["\n  height: 100%;\n"])));
var templateObject_1, templateObject_2, templateObject_3, templateObject_4;
